import { createFileRoute, Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { StatusBadge } from '@/components/StatusBadge';
import { useCancelJob, useJob, useResumeJob } from '@/lib/api/queries';
import { formatInt } from '@/lib/format';

function RunMonitor() {
  const { runId } = Route.useParams();
  const { data: job } = useJob(runId);
  const cancel = useCancelJob();
  const resume = useResumeJob();

  if (!job) return <p className="text-sm text-secondary">Chargement du run…</p>;

  const pct = job.tasks_total > 0 ? Math.round((job.tasks_done / job.tasks_total) * 100) : 0;
  const canCancel = ['running', 'pending', 'paused'].includes(job.status);
  const canResume = ['paused', 'cancelled', 'cancelling'].includes(job.status);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to="/runs" className="text-xs text-secondary hover:text-accent">
            ← Tous les runs
          </Link>
          <h1 className="truncate text-2xl font-extrabold tracking-tight">{job.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={job.status} />
          {canCancel ? (
            <button
              type="button"
              onClick={() =>
                cancel.mutate(job.id, {
                  onSuccess: () => toast.success('Annulation demandée'),
                  onError: (e) => toast.error(e.message),
                })}
              className="rounded-control border border-hairline px-3 py-1.5 text-sm font-semibold"
            >
              Annuler
            </button>
          ) : null}
          {canResume ? (
            <button
              type="button"
              onClick={() =>
                resume.mutate(job.id, {
                  onSuccess: () => toast.success('Run repris — les pages déjà payées ne sont pas refaites'),
                  onError: (e) => toast.error(e.message),
                })}
              className="rounded-control bg-accent px-3 py-1.5 text-sm font-semibold text-white"
            >
              Reprendre
            </button>
          ) : null}
          <a
            href={`/api/jobs/${job.id}/export.csv`}
            className="rounded-control border border-hairline px-3 py-1.5 text-sm font-semibold"
          >
            Export CSV
          </a>
        </div>
      </div>

      {job.error_message ? (
        <p className="rounded-card bg-tint-pink px-4 py-3 text-sm text-danger">
          {job.error_message}
        </p>
      ) : null}

      <div className="rounded-card bg-surface p-5 shadow-card">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold">Avancement</span>
          <span className="tabular-nums text-secondary">
            {formatInt(job.tasks_done)} / {formatInt(job.tasks_total)} zones · {pct} %
          </span>
        </div>
        <div className="mt-3 h-3 overflow-hidden rounded-full bg-sunken">
          <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ['Fiches trouvées', job.places_found],
          ['Leads nouveaux', job.places_new],
          ['Appels API', job.api_calls_used],
          ['Zones en échec', job.tasks_failed],
        ].map(([label, value]) => (
          <div key={label as string} className="rounded-card bg-surface p-5 shadow-card">
            <p className="text-xs uppercase tracking-wide text-secondary">{label}</p>
            <p className="mt-2 text-2xl font-extrabold tabular-nums">
              {formatInt(value as number)}
            </p>
          </div>
        ))}
      </div>

      <Link
        to="/leads"
        search={{ runId: job.id }}
        className="rounded-card bg-surface p-4 text-sm font-semibold shadow-card hover:text-accent"
      >
        Voir les {formatInt(job.places_new)} leads de ce run →
      </Link>
    </div>
  );
}

export const Route = createFileRoute('/runs/$runId')({ component: RunMonitor });
